/* eslint complexity: ["error", 60]*/
import React from 'react';

import { Grid } from '../../../common/components/Grid/Grid';
import {
  GridColumn,
  GridCountFunction,
  GridGetterFunction,
} from '../../../common/components/Grid/GridDataTypes';
import { useTranslation } from '../../../common/hooks/useTranslation';
import {
  GetEmailWithSalesInvoiceDto,
  GetEmailWithSalesInvoiceDtoEmailTypeEnum,
} from '../../../common/pokCore/autogenerated/pokApiClient';
import momencik from '../../../common/momencik';
import { NewWindowButton } from '../SalesInvoices/NewWindowButton';
import { AddEditEmailButton } from '../EmailAddress/AddEmailButton';

import { EmailResendLink } from './EmailResendLink';
import { ChooseEmailButton } from './ChooseEmailButton';
import { DuplicateInvoice } from './DuplicateInvoice';

interface AlreadySentEmailsSearchProps {
  getData: GridGetterFunction;
  getCount: GridCountFunction;
  onRowClick?: (key: string) => void;
  refresh?: boolean;
}

export const alreadySentEmailsSearchGrid = (
  sentEmail: GetEmailWithSalesInvoiceDto,
  refreshData: () => void,
) => {
  const salesInvoices = sentEmail.salesInvoices;
  const salesInvoice = salesInvoices?.length ? salesInvoices[0] : null;
  const purchaserEmailAddresses =
    salesInvoice?.purchaser?.client?.emailAddresses?.filter(e => e.active);
  return {
    key: sentEmail.id,
    values: {
      'salesInvoice.systemNumber': salesInvoices
        ?.map(o => o.systemNumber)
        .join(', '),
      'salesInvoice.number': salesInvoices?.map(o => o.number).join(', '),
      'salesInvoice.invoiceDate':
        salesInvoices?.length === 1 && salesInvoice
          ? momencik(salesInvoice.invoiceDate)
          : '',
      'salesInvoice.saleDate':
        salesInvoices?.length === 1 && salesInvoice
          ? momencik(salesInvoice.saleDate)
          : '',
      'emails.sendDate': momencik(sentEmail.sendDate, 'YYYY-MM-DD HH:mm:ss'),
      'client.name': salesInvoice?.purchaser?.client?.name,
      'emails.sentTo': sentEmail.sentTo,
      editAddress: (
        <>
          <div className="d-flex align-items-center">
            {(salesInvoice?.emailAddress?.active ||
              !purchaserEmailAddresses ||
              purchaserEmailAddresses?.length <= 1) && (
              <AddEditEmailButton
                emailAddress={
                  purchaserEmailAddresses?.length === 1
                    ? purchaserEmailAddresses[0]
                    : salesInvoice?.emailAddress?.active
                      ? salesInvoice?.emailAddress
                      : undefined
                }
                clientId={salesInvoice?.purchaser?.client?.id}
                client={salesInvoice?.purchaser?.client || undefined}
                refresh={true}
                setRefresh={() => {
                  refreshData();
                }}
              />
            )}
            {purchaserEmailAddresses && purchaserEmailAddresses?.length > 1 && (
              <ChooseEmailButton
                clientId={salesInvoice?.purchaser?.client?.id}
                salesInvoiceId={salesInvoice?.id}
                refresh={true}
                setRefresh={() => {
                  refreshData();
                }}
              />
            )}
            {sentEmail.emailType ===
              GetEmailWithSalesInvoiceDtoEmailTypeEnum.SalesInvoiceDuplicateToContractor && (
              <DuplicateInvoice />
            )}
          </div>
        </>
      ),
      'emails.sentCc': sentEmail.sentCc,
      'emails.sentFrom': sentEmail.sentFrom,
      newWindowButton: <NewWindowButton salesInvoiceId={salesInvoice?.id} />,
      resend: (
        <>
          {salesInvoice && (
            <EmailResendLink
              emailAddresses={
                purchaserEmailAddresses?.length === 1
                  ? purchaserEmailAddresses[0]
                  : salesInvoice?.emailAddress?.active
                    ? salesInvoice?.emailAddress
                    : undefined
              }
              emailId={sentEmail.id}
              invoiceIds={salesInvoices?.map(o => o?.id) || []}
              refreshData={refreshData}
            />
          )}
        </>
      ),
    },
  };
};

export const AlreadySentEmailsSearch: React.FC<
  AlreadySentEmailsSearchProps
> = props => {
  const { t, tk } = useTranslation('fvsLng');

  const columns: GridColumn[] = [
    {
      header: '',
      property: 'newWindowButton',
      noSorting: true,
      noFilter: true,
      columnCssClass: 'icon-in-grid',
    },
    { header: t(tk.common.sendDate), property: 'emails.sendDate' },
    {
      header: t(tk.finance.sysNumber),
      property: 'salesInvoice.systemNumber',
      noSorting: true,
    },
    {
      header: t(tk.finance.invoiceNumber),
      property: 'salesInvoice.number',
      noSorting: true,
    },
    {
      header: t(tk.common.issueDate),
      property: 'salesInvoice.invoiceDate',
      noSorting: true,
    },
    {
      header: t(tk.common.saleDate),
      property: 'salesInvoice.saleDate',
      noSorting: true,
    },
    { header: t(tk.sendEmail.to), property: 'emails.sentTo' },
    { header: t(tk.sendEmail.cc), property: 'emails.sentCc' },
    { header: t(tk.sendEmail.emailFrom), property: 'emails.sentFrom' },
    {
      header: t(tk.finance.buyerName),
      property: 'client.name',
      noSorting: true,
    },
    {
      header: '',
      property: 'editAddress',
      noFilter: true,
      noSorting: true,
    },
    {
      header: '',
      property: 'resend',
      noSorting: true,
      noFilter: true,
      columnCssClass: 'icon-in-grid',
    },
  ];

  return (
    <Grid
      getData={props.getData}
      getDataCount={props.getCount}
      columns={columns}
      showFilter={true}
      initialOrderColumn="emails.sendDate"
      initialOrderDirection="DESC"
      availablePageSizes={[10, 20, 50]}
      emptyGridMessage={t(tk.finance.noSentInvoices)}
      withHistory={true}
      onRowClick={props.onRowClick}
      refresh={props.refresh}
      withFilterInColumns={true}
      withOverflowXAuto={true}
    />
  );
};
